// PDF LAYOUT RECONSTRUCTION (client-side, pdf.js geometry) — the fix for "her PDF couldn't be read properly".
//
// pdf.js's `getTextContent()` returns positioned text RUNS, not lines. Until this module existed, `cvParse.ts`
// joined every run with a single space and then collapsed ALL whitespace, so a whole page became ONE flat line:
// no line breaks, no bullets on their own lines, no section / entry boundaries, and a two-column CV interleaved
// left and right runs word by word. The segmenter (/api/parse) and the contact-header rule ("the lines before
// the first section") both depend on line structure, so a perfectly ordinary PDF CV read as garbage.
//
// This module rebuilds that structure from the run geometry pdf.js DOES give us (`transform`, `width`,
// `hasEOL`), as a PURE function over an abstract item shape so the suite can exercise it with synthetic
// fixtures and no pdf.js:
//
//   1. COLUMNS. Per page, find a vertical gutter no run crosses, with substantial text on BOTH sides. If one
//      exists, read the left column top→bottom, then the right — never interleaved. A right-aligned date column
//      is NOT a column: the right side has to carry a real share of the page's characters and its runs have to
//      be more than short labels (see `isRealColumn`), otherwise the dates stay on the line they belong to.
//   2. LINES. Within a column, runs are grouped into lines by baseline y (tolerance 0.4 × font size) and sorted
//      by x. Adjacent runs are joined with NOTHING when they abut (gap < 0.25 em), ONE space for a word gap,
//      and a DOUBLE space for a tab-like gap (> 2 em — the right-aligned date on a heading line), so the date
//      stays attached to its heading but visibly separated from it.
//   3. BLANK LINES. A vertical gap > 1.6 × the line height is a paragraph / entry / section boundary and
//      becomes one blank line. Never more than one: runs of blank lines carry no more structure than one does.
//   4. INDENTATION. A line whose x-start sits > 1 em right of the column's dominant left edge (and does not
//      already start with a bullet glyph) is prefixed with two spaces, so a hanging-indent continuation or a
//      sub-point keeps a visible marker. Bullet glyphs (•, ●, ◦, ▪, -, –) are in the run text and are kept.
//
// OFFSET CONTRACT. The output is a list of SEPARATORS (one per item in reading order, the string to place
// BEFORE that item) rather than a joined string, because the hidden-region detector in
// `workers/src/lib/hiddenRegionDetect.ts` reports code-point offsets into the text IT joins. `cvParse.ts` hands
// the detector the reordered items AND these separators, so the reconstructed text and the detected offsets
// are one and the same string — detection never runs on a text that is then re-flowed underneath it.
//
// FALLBACK. Items with no usable geometry (no transform) — which real pdf.js never produces, but the suite's
// lightweight mocks do — are joined in their given order with a single space, and a newline after any item
// pdf.js flagged `hasEOL`. That is the pre-fix behaviour, kept only for the no-geometry case.

export interface LayoutItem {
  str: string;
  transform?: readonly number[];
  width?: number;
  height?: number;
  hasEOL?: boolean;
}

export interface LayoutResult<T extends LayoutItem> {
  /** The items in READING order (columns, then lines, then x). Whitespace-only items are dropped. */
  items: T[];
  /** `separators[i]` is the string placed immediately BEFORE `items[i]` when joining. `separators[0]` is ''. */
  separators: string[];
  /** How many columns the page was read as (1 when no real gutter was found). */
  columns: number;
}

/** Join a layout result into the page text — the same join `detectPdfHiddenRegions` performs with these separators. */
export function joinLayout<T extends LayoutItem>(r: LayoutResult<T>): string {
  let out = '';
  for (let i = 0; i < r.items.length; i++) out += r.separators[i] + r.items[i].str;
  return out;
}

const BULLET_RE = /^\s*[•●◦▪■○\-–—*·]/u;
const LONE_BULLET_RE = /^\s*[•●◦▪■○*·]\s*$/u;
const PLAIN_SPACE_RE = /^[ \t ]*$/;

interface Geo<T> { item: T; x: number; y: number; size: number; width: number; chars: number }

function geoOf<T extends LayoutItem>(it: T): Geo<T> | null {
  const t = it.transform;
  if (!t || t.length < 6 || !t.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  const size = Math.abs(it.height ?? t[3]) || Math.abs(t[0]) || 10;
  const chars = Array.from(it.str).length;
  const width = typeof it.width === 'number' && Number.isFinite(it.width) && it.width >= 0
    ? it.width
    : chars * size * 0.5; // crude glyph-width estimate when pdf.js did not supply one (fixtures)
  return { item: it, x: t[4], y: t[5], size, width, chars };
}

/** Keep an item iff it carries visible text OR a zero-width/control run the hidden-text detector must still see. */
function isContent(it: LayoutItem): boolean {
  const s = it.str ?? '';
  return s.length > 0 && !PLAIN_SPACE_RE.test(s);
}

interface Line<T> { y: number; size: number; x0: number; runs: Geo<T>[] }

function groupLines<T extends LayoutItem>(geos: Geo<T>[]): Line<T>[] {
  const sorted = [...geos].sort((a, b) => b.y - a.y || a.x - b.x);
  const lines: Line<T>[] = [];
  for (const g of sorted) {
    const cur = lines[lines.length - 1];
    if (cur && Math.abs(cur.y - g.y) <= 0.4 * Math.max(cur.size, g.size)) {
      cur.runs.push(g);
      cur.size = Math.max(cur.size, g.size);
      cur.x0 = Math.min(cur.x0, g.x);
    } else {
      lines.push({ y: g.y, size: g.size, x0: g.x, runs: [g] });
    }
  }
  for (const ln of lines) ln.runs.sort((a, b) => a.x - b.x);
  return lines;
}

/**
 * Find a vertical gutter splitting the page's runs into real columns. Returns the x positions of the gutters
 * (0 or more). A "real" column on each side must (a) span a substantial share of the page's text height,
 * (b) hold a real share of the page's characters, and (c) not be made of short labels only — that last rule
 * is what keeps a right-aligned date column attached to its headings.
 */
function findGutters<T extends LayoutItem>(geos: Geo<T>[], pageWidth: number): number[] {
  if (geos.length < 6) return [];
  const xMin = Math.min(...geos.map((g) => g.x));
  const xMax = Math.max(...geos.map((g) => g.x + g.width));
  const span = xMax - xMin;
  if (!(span > 0)) return [];
  const yMin = Math.min(...geos.map((g) => g.y));
  const yMax = Math.max(...geos.map((g) => g.y));
  const ySpan = Math.max(1, yMax - yMin);
  const totalChars = geos.reduce((n, g) => n + g.chars, 0);
  const minGutter = Math.max(6, Math.min(pageWidth, span) * 0.02);

  // Candidate gutters are the gaps in the union of x-intervals covered by runs.
  const ivs = geos.map((g) => [g.x, g.x + g.width] as const).sort((a, b) => a[0] - b[0]);
  const gaps: Array<[number, number]> = [];
  let reach = ivs[0][1];
  for (let i = 1; i < ivs.length; i++) {
    const [a, b] = ivs[i];
    if (a - reach >= minGutter) gaps.push([reach, a]);
    reach = Math.max(reach, b);
  }

  const gutters: number[] = [];
  for (const [g0, g1] of gaps) {
    const gx = (g0 + g1) / 2;
    // Ignore gutters hugging the page edge — those are margins / stray marks, not column separators.
    if (gx - xMin < span * 0.15 || xMax - gx < span * 0.15) continue;
    const left = geos.filter((g) => g.x + g.width <= gx);
    const right = geos.filter((g) => g.x >= gx);
    if (!isRealColumn(left, ySpan, totalChars) || !isRealColumn(right, ySpan, totalChars)) continue;
    gutters.push(gx);
  }
  return gutters;
}

const DATEISH_RE = /(\b(19|20)\d{2}\b|\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\b|present|ongoing|current)/i;

function isRealColumn<T extends LayoutItem>(side: Geo<T>[], ySpan: number, totalChars: number): boolean {
  if (side.length < 3) return false;
  const ys = side.map((g) => g.y);
  const sideSpan = Math.max(...ys) - Math.min(...ys);
  if (sideSpan < 0.3 * ySpan) return false; // not a column, a caption / footer / one block
  const chars = side.reduce((n, g) => n + g.chars, 0);
  if (chars < 0.2 * totalChars) return false; // a date column or marginal labels, not a column of content
  const lines = groupLines(side).map((ln) => ln.runs.map((r) => r.item.str).join(' ').trim());
  if (lines.length < 3) return false;
  const sortedLens = lines.map((l) => l.length).sort((a, b) => a - b);
  if (sortedLens[Math.floor(sortedLens.length / 2)] < 8) return false; // icons / single glyph rail
  // A RIGHT-ALIGNED DATE RAIL ("2021 – 2023", "Oct 2024 (4 weeks)", "09/2018 – Present") is the classic false
  // column: short, date-ish lines, one per heading. It belongs ON the heading line, so it is not a column.
  const dateish = lines.filter((l) => l.length <= 28 && DATEISH_RE.test(l)).length;
  return dateish / lines.length < 0.6;
}

/**
 * Reconstruct reading order and separators for one page of pdf.js text items. Pure; see the module comment.
 */
export function layoutPdfItems<T extends LayoutItem>(items: readonly T[], pageWidth = Infinity): LayoutResult<T> {
  const kept: T[] = [];
  const eolCarry: boolean[] = []; // fallback only: an EOL on a dropped whitespace item moves to the item before it
  for (const it of items) {
    if (isContent(it)) { kept.push(it); eolCarry.push(Boolean(it.hasEOL)); }
    else if (it.hasEOL && eolCarry.length) eolCarry[eolCarry.length - 1] = true;
  }
  if (kept.length === 0) return { items: [], separators: [], columns: 1 };

  const geos = kept.map(geoOf);
  if (geos.some((g) => g === null)) {
    // FALLBACK — no geometry to reason with. Single-space join, newline after hasEOL (pre-fix behaviour).
    const separators = kept.map((_, i) => (i === 0 ? '' : eolCarry[i - 1] ? '\n' : ' '));
    return { items: kept, separators, columns: 1 };
  }
  const all = geos as Geo<T>[];
  const gutters = findGutters(all, Number.isFinite(pageWidth) ? pageWidth : Math.max(...all.map((g) => g.x + g.width)));
  const bounds = [-Infinity, ...gutters, Infinity];
  const columns: Geo<T>[][] = [];
  for (let c = 0; c < bounds.length - 1; c++) {
    const col = all.filter((g) => g.x >= bounds[c] && g.x < bounds[c + 1]);
    if (col.length) columns.push(col);
  }

  const outItems: T[] = [];
  const separators: string[] = [];
  let prevLine: Line<T> | null = null;
  for (const col of columns) {
    const lines = groupLines(col);
    // Dominant left edge of the column — the mode of line x-starts (rounded) — for the indentation marker.
    const counts = new Map<number, number>();
    for (const ln of lines) { const k = Math.round(ln.x0); counts.set(k, (counts.get(k) ?? 0) + 1); }
    let left = lines[0]?.x0 ?? 0; let best = -1;
    for (const [k, n] of counts) if (n > best || (n === best && k < left)) { best = n; left = k; }
    const colRight = Math.max(...col.map((g) => g.x + g.width));
    const colCenter = (left + colRight) / 2;

    for (let li = 0; li < lines.length; li++) {
      const ln = lines[li];
      // Line-start separator: first item of the page '', else '\n' or '\n\n' (big vertical gap = boundary).
      let lead: string;
      if (outItems.length === 0) lead = '';
      else if (prevLine === null) lead = '\n\n'; // column switch is always a boundary
      else {
        const gap = prevLine.y - ln.y;
        lead = gap > 1.6 * Math.max(prevLine.size, ln.size) ? '\n\n' : '\n';
      }
      // Indentation marker: a modest inset from the column's left edge (a hanging indent / sub-point), but NOT a
      // centered line (the contact header) and not a line that already starts with a bullet glyph.
      const first = ln.runs[0];
      const last = ln.runs[ln.runs.length - 1];
      const inset = ln.x0 - left;
      const lineCenter = (ln.x0 + last.x + last.width) / 2;
      const nearCenter = Math.abs(lineCenter - colCenter) < 2 * ln.size
        || (columns.length === 1 && Number.isFinite(pageWidth) && Math.abs(lineCenter - pageWidth / 2) < 2 * ln.size);
      const centered = nearCenter && inset > 4 * ln.size;
      if (inset > 1.0 * ln.size && inset <= 10 * ln.size && !centered && !BULLET_RE.test(first.item.str)) lead += '  ';

      for (let ri = 0; ri < ln.runs.length; ri++) {
        const r = ln.runs[ri];
        if (ri === 0) { outItems.push(r.item); separators.push(lead); continue; }
        const p = ln.runs[ri - 1];
        const gap = r.x - (p.x + p.width);
        const em = Math.max(p.size, r.size);
        let sep: string;
        if (LONE_BULLET_RE.test(p.item.str)) sep = ' '; // "• text", never "•text" — the marker is its own run
        else if (/\s$/.test(p.item.str) || /^\s/.test(r.item.str)) sep = '';
        else if (gap < 0.25 * em) sep = '';
        else if (gap > 2 * em) sep = '  ';
        else sep = ' ';
        outItems.push(r.item); separators.push(sep);
      }
      prevLine = ln;
    }
    prevLine = null;
  }
  return { items: outItems, separators, columns: columns.length };
}

/** Flat-text heuristic for the user-facing steer: too few lines, or most of the page on one line. */
export function looksFlat(text: string): boolean {
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  if (lines.length < 8) return true;
  const total = lines.reduce((n, l) => n + l.length, 0);
  const longest = Math.max(...lines.map((l) => l.length));
  return total > 0 && longest / total > 0.6;
}
